import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";

export const ModalOverlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

export const ModalContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  max-height: 90vh;
  max-width: 920px;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  width: 90vw;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 4px;
  }
`;

export const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Header = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

export const Title = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 28px;
  font-weight: 600;
  margin: 0;
`;

export const GameLogo = styled.img`
  height: 26px;
  width: 26px;
`;

export const SettingRow = styled(Flex)<{ $disabled?: boolean }>`
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  padding: 14px 0;
  transition: opacity 0.15s ease;
  width: 100%;
`;

export const SettingInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

export const SettingLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 500;
  gap: 10px;
`;

export const SettingDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-family: Barlow;
  font-size: 14px;
  padding-left: 0;
`;

export const ColorGrid = styled(Box)<{ $disabled?: boolean }>`
  display: flex;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: opacity 0.15s ease;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

export const ColorSwatch = styled.button<{
  $swatch: string;
  $selected: boolean;
}>`
  background: ${({ $swatch }) => $swatch};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  height: 22px;
  width: 22px;
  box-shadow: ${({ $selected, theme }) =>
    $selected ? `0 0 0 2px ${theme.colors.envyAlpha30}` : "none"};
  cursor: pointer;
  padding: 0;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const Footer = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export const AllSettingsLink = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 2px;
  margin-right: auto;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const SaveFeedback = styled(Flex)<{ $success: boolean }>`
  align-items: center;
  color: ${({ $success }) => ($success ? "#4dbd94" : "#e74c3c")};
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
  gap: 4px;
`;

export const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-family: "Barlow", sans-serif;
  font-size: 17px;
  font-weight: 600;
  padding: 10px 20px;
  transition:
    opacity 0.2s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const CodeField = styled(Box)`
  margin: 4px 0 0 34px;
  max-width: 100%;
  width: calc(100% - 34px);
`;

export const CodeHint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-family: Barlow;
  font-size: 13px;
  margin-top: 6px;
`;

export const CodeInput = styled.input`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  margin-bottom: 8px;
  padding: 8px 10px;
  width: 100%;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }
`;

export const PreviewColumn = styled(Flex)`
  align-self: flex-start;
  flex: 0 0 480px;
  flex-direction: column;

  @media (max-width: 860px) {
    align-self: stretch;
    flex: 0 0 auto;
    order: -1;
    width: 100%;
  }
`;

// Fixed 16:9 box. Explicit height rather than aspect-ratio so flex-shrink inside
// the modal cannot squash it and throw the clip-path regions out of line.
export const Preview = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  cursor: zoom-in;
  flex: 0 0 auto;
  height: 270px;
  overflow: hidden;
  position: relative;
  width: 100%;

  @media (max-width: 860px) {
    height: auto;
    padding-top: 56.25%;
  }
`;

export const PreviewNote = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-family: Barlow;
  font-size: 15px;
  line-height: 1.5;
  margin-top: 12px;
`;

// Wraps the layer stack so a hover magnifier can scale them together.
export const PreviewStage = styled.div`
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: transform 0.15s ease-out;
  width: 100%;
`;

export const PreviewLayer = styled.img<{ $clip?: string }>`
  height: 100%;
  left: 0;
  object-fit: fill;
  position: absolute;
  top: 0;
  width: 100%;
  ${({ $clip }) => ($clip ? `clip-path: inset(${$clip});` : "")}
`;

export const PresetRow = styled(Flex)`
  gap: 8px;
  padding-bottom: 6px;
`;

export const PresetButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  padding: 7px 16px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const Layout = styled(Flex)`
  align-items: flex-start;
  gap: 20px;

  @media (max-width: 860px) {
    flex-direction: column;
  }
`;

export const TabBar = styled(Flex)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  gap: 4px;
  margin-bottom: 20px;
  padding: 4px;
`;

export const NavItem = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.envy : "transparent"};
  border: none;
  border-radius: 6px;
  color: ${({ $active, theme }) => ($active ? "white" : theme.colors.ice)};
  cursor: pointer;
  display: flex;
  flex: 1;
  font-family: Barlow;
  font-size: 16px;
  font-weight: 600;
  gap: 8px;
  justify-content: center;
  padding: 10px 14px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha10};
  }
`;

export const Controls = styled(Box)`
  flex: 1;
  overflow-x: hidden;
  max-height: min(380px, 55vh);
  min-width: 0;
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
  }
`;

export const OptionRow = styled.button<{ $selected: boolean }>`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  gap: 12px;
  padding: 12px 0;
  text-align: left;
  width: 100%;

  &:hover [data-radio] {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

export const RadioDot = styled(Box)<{ $selected: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 12px;
  width: 12px;
`;

export const HeaderText = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
`;

export const Toggle = styled(Flex)<{ $on: boolean }>`
  align-items: center;
  background: ${({ $on, theme }) =>
    $on ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
  height: 26px;
  padding: 3px;
  transition: background 0.18s ease;
  width: 46px;
`;

export const ToggleKnob = styled(Box)<{ $on: boolean }>`
  background: ${({ theme }) => theme.colors.ice};
  border-radius: 50%;
  height: 20px;
  transform: translateX(${({ $on }) => ($on ? "20px" : "0")});
  transition: transform 0.18s ease;
  width: 20px;
`;

export const SectionHeader = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  margin-top: 12px;
  text-transform: uppercase;
`;

export const Divider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  height: 1px;
  margin: 12px 0 4px;
  width: 100%;
`;

export const Select = styled.select`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  flex-shrink: 0;
  font-family: Barlow;
  font-size: 15px;
  padding: 6px 10px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }

  option {
    background: ${({ theme }) => theme.colors.ned};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export const SectionIntro = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-family: Barlow;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 6px;
`;
